/**
 * WS-ELIG B-E5 (constitution closure 2026-10-19) — "reassign needed" on the Dayboard.
 *
 * CORE-AD:45: the board SHALL surface the operational state that governs the day so a problem is visible before
 * someone reports it. CORE-AD:49: it MUST NOT decide anything on its own. CORE-AB:84: where an assignment cannot be
 * satisfied the system says so rather than quietly ranking someone else.
 *
 * In a shop that ENFORCES real employee eligibility (W3), a ticket can still be held by someone who stopped being an
 * operational worker after it was assigned (left, suspended, invitation never accepted, no work role). The
 * assignment is kept — history is never rewritten (CORE-W:47) and nothing is reassigned automatically — and this
 * panel lists that work so a person decides. The list and its audience are the SERVER's (list_reassign_needed:
 * approvers + dispatchers; an observe shop returns nothing). Read-only: it writes nothing.
 */
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useScheduleSignalRefresh } from '@/hooks/useScheduleSignalRefresh';
import { useLatestResponseGuard } from '@/hooks/useLatestResponseGuard';
import { getScheduleDock } from '@/components/scheduling/scheduleDock';
import { WORK_ELIGIBILITY_REASON_LABEL, isWorkEligibilityReason } from '@/lib/roles/workEligibility';

export interface ReassignNeededRow {
  ticket_id: string;
  simple_ticket_number: number | null;
  scheduled_date: string | null;
  start_time: string | null;
  held_as: 'assigned' | 'scheduled';
  technician_id: string;
  technician_name: string | null;
  reason: string;
  in_progress: boolean;
}

/** Plain-language reason a holder may not keep receiving work. */
export function describeReassignReason(reason: string): string {
  return isWorkEligibilityReason(reason) ? WORK_ELIGIBILITY_REASON_LABEL[reason] : 'Not able to receive new work';
}

// The generated types do not know this RPC; one narrow cast, the client is never detached.
type LooseRpc = { rpc: (f: string, a: Record<string, unknown>) => PromiseLike<{ data: unknown; error: unknown }> };

export function ReassignNeededPanel() {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id ?? null;
  const [rows, setRows] = useState<ReassignNeededRow[]>([]);
  const [allowed, setAllowed] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const { begin } = useLatestResponseGuard();   // Issue #26: an older answer never replaces a newer one
  const load = useCallback(async () => {
    if (!shopId) return;
    const isLatest = begin();
    try {
      const { data, error } = await (supabase as unknown as LooseRpc).rpc('list_reassign_needed', { p_shop: shopId });
      if (!isLatest()) return;
      if (error) {
        // 42501 = not an approver / dispatcher of this shop: hide. Any other read failure keeps the last list.
        if ((error as { code?: string }).code === '42501') { setAllowed(false); setRows([]); }
        return;
      }
      setAllowed(true);
      setRows(Array.isArray(data) ? (data as ReassignNeededRow[]) : []);
    } catch {
      /* a failed READ keeps the last list; nothing is written */
    }
  }, [shopId, begin]);

  useEffect(() => {
    if (!shopId) return;
    void load();
    const t = setInterval(() => { void load(); }, 60_000);
    return () => clearInterval(t);
  }, [shopId, load]);
  useScheduleSignalRefresh(() => { void load(); }, { enabled: !!shopId });

  if (!allowed || rows.length === 0 || typeof document === 'undefined') return null;
  const n = rows.length;

  return createPortal((
    <div role="region" aria-label="Reassign needed" data-testid="reassign-needed-panel"
      className="pointer-events-auto order-1 w-full max-w-[26rem] rounded-lg border-2 border-amber-500 bg-amber-50 text-amber-950 shadow-lg dark:bg-amber-950 dark:text-amber-50">
      <button type="button" className="flex w-full items-center justify-between px-4 py-2 text-left font-medium"
        onClick={() => setExpanded((e) => !e)} aria-expanded={expanded} data-testid="reassign-needed-toggle">
        <span>Reassign needed: {n} ticket{n === 1 ? '' : 's'} held by someone who cannot receive work</span>
        <span aria-hidden>{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded && (
        <ul className="max-h-[22vh] lg:max-h-[50vh] space-y-2 overflow-y-auto px-4 pb-4 text-sm">
          {rows.map((r) => (
            <li key={`${r.ticket_id}:${r.held_as}`} data-testid="reassign-needed-row" data-reason={r.reason}>
              <div className="font-medium">
                {r.simple_ticket_number ? `#${r.simple_ticket_number}` : r.ticket_id}
                {r.scheduled_date ? ` · ${r.scheduled_date}` : ''}
              </div>
              <div>
                {r.held_as === 'scheduled' ? 'Scheduled for' : 'Assigned to'} {r.technician_name ?? 'someone not on this team'}
                {' — '}{describeReassignReason(r.reason)}
              </div>
              <div className="opacity-80">
                {r.in_progress
                  ? 'Work already started stays with them and can be wrapped up. Choose who takes the rest.'
                  : 'The assignment was kept. Nothing was moved automatically — choose who should take it.'}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  ), getScheduleDock());
}
